import { useMemo, useState } from "react";
import type { CreditBalance, CreditPool, UserProfile } from "@chuzi/shared/types";
import {
  defaultPurchaseCredits,
  formatCreditPrice,
  priceCentsForCredits,
  t as tNeutral,
} from "@chuzi/shared/config";
import { useRealmTheme } from "@chuzi/shared/ui";
import { ChuziApiError } from "@chuzi/shared/api";
import { CosmosModal } from "./cosmos-modal";
import { api } from "../lib/api";

const INPUT_STYLE: React.CSSProperties = {
  width: "100%",
  background: "rgba(126,184,255,0.06)",
  border: "1px solid rgba(126,184,255,0.35)",
  borderRadius: 8,
  color: "#e8f0ff",
  padding: "8px 10px",
  fontSize: 13,
  outline: "none",
};

const FIELD_LABEL: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 1,
  color: "rgba(232,240,255,0.55)",
  marginBottom: 4,
  display: "block",
};

function initialCreditsForPool(
  pool: CreditPool,
  sourcePool: CreditPool,
  creditsNeeded: number,
): number {
  if (pool !== sourcePool) {
    return 0;
  }
  return Math.max(creditsNeeded, defaultPurchaseCredits(pool));
}

export function SpendingModal({
  user,
  pool: sourcePool,
  creditsNeeded = 1,
  onClose,
  onSuccess,
}: {
  user: UserProfile | null;
  pool: CreditPool;
  creditsNeeded?: number;
  onClose: () => void;
  onSuccess: (balance: CreditBalance) => void;
}) {
  const { t, locale } = useRealmTheme();
  const [createCredits, setCreateCredits] = useState(() =>
    initialCreditsForPool("create", sourcePool, creditsNeeded),
  );
  const [watchCredits, setWatchCredits] = useState(() =>
    initialCreditsForPool("watch", sourcePool, creditsNeeded),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = !!user?.is_admin;
  const totalCredits = createCredits + watchCredits;
  const priceCents = useMemo(() => {
    if (isAdmin) return 0;
    return (
      priceCentsForCredits("create", createCredits) +
      priceCentsForCredits("watch", watchCredits)
    );
  }, [isAdmin, createCredits, watchCredits]);

  async function ensurePaymentMethodId(): Promise<string | undefined> {
    if (isAdmin) return undefined;
    const methods = await api.payments.list();
    const existing =
      methods.payment_methods.find((m) => m.is_default) ??
      methods.payment_methods[0] ??
      null;
    if (existing) return existing.id;
    const stored = await api.payments.store({
      last_four: "4242",
      label: "Dev stub",
    });
    return stored.payment_method.id;
  }

  async function handleSubmit() {
    if (!user) return;
    if (createCredits < 1 && watchCredits < 1) return;

    setError(null);
    setSubmitting(true);

    try {
      let balance: CreditBalance | null = null;

      if (isAdmin) {
        if (createCredits >= 1) {
          const res = await api.admin.credits.grant({
            user_id: user.id,
            pool: "create",
            amount: createCredits,
            note: "self-grant via spending modal",
          });
          balance = res.balance;
        }
        if (watchCredits >= 1) {
          const res = await api.admin.credits.grant({
            user_id: user.id,
            pool: "watch",
            amount: watchCredits,
            note: "self-grant via spending modal",
          });
          balance = res.balance;
        }
      } else {
        const paymentMethodId = await ensurePaymentMethodId();
        if (createCredits >= 1) {
          const res = await api.credits.purchaseAmount({
            pool: "create",
            credits: createCredits,
            payment_method_id: paymentMethodId,
          });
          balance = res.balance;
        }
        if (watchCredits >= 1) {
          const res = await api.credits.purchaseAmount({
            pool: "watch",
            credits: watchCredits,
            payment_method_id: paymentMethodId,
          });
          balance = res.balance;
        }
      }

      if (balance) {
        onSuccess(balance);
      }
    } catch (err) {
      if (err instanceof ChuziApiError) {
        const body = err.body as { error?: string };
        setError(body?.error ?? t("spending_modal_error"));
      } else {
        setError(t("spending_modal_error"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const primaryLabel = isAdmin
    ? t("spending_modal_grant")
    : t("spending_modal_purchase");

  return (
    <CosmosModal
      title={t("spending_modal_title")}
      onClose={onClose}
      width={480}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={secondaryBtnStyle}
          >
            {t("spending_modal_cancel")}
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={submitting || totalCredits < 1}
            style={{
              ...primaryBtnStyle,
              opacity: submitting || totalCredits < 1 ? 0.5 : 1,
              cursor: submitting || totalCredits < 1 ? "default" : "pointer",
            }}
          >
            {submitting ? t("spending_modal_processing") : primaryLabel}
          </button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <p style={{ margin: 0, fontSize: 13, color: "rgba(232,240,255,0.75)", lineHeight: 1.5 }}>
          {t("spending_modal_body")}
        </p>

        <div>
          <label style={FIELD_LABEL}>
            {tNeutral(null, "creator_credits", "", locale)}
          </label>
          <input
            type="number"
            min={0}
            max={10000}
            step={1}
            value={createCredits}
            onChange={(e) => {
              const next = Number.parseInt(e.target.value, 10);
              setCreateCredits(Number.isFinite(next) ? Math.max(0, next) : 0);
            }}
            style={INPUT_STYLE}
          />
        </div>

        <div>
          <label style={FIELD_LABEL}>
            {tNeutral(null, "viewer_credits", "", locale)}
          </label>
          <input
            type="number"
            min={0}
            max={10000}
            step={1}
            value={watchCredits}
            onChange={(e) => {
              const next = Number.parseInt(e.target.value, 10);
              setWatchCredits(Number.isFinite(next) ? Math.max(0, next) : 0);
            }}
            style={INPUT_STYLE}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 12px",
            borderRadius: 8,
            background: "rgba(126,184,255,0.06)",
            border: "1px solid rgba(126,184,255,0.2)",
          }}
        >
          <span style={{ fontSize: 12, color: "rgba(232,240,255,0.55)" }}>
            {t("spending_modal_price_label")}
          </span>
          <span style={{ fontSize: 15, fontWeight: 700 }}>
            {isAdmin ? "—" : formatCreditPrice(priceCents)}
          </span>
        </div>

        {isAdmin ? (
          <p style={{ margin: 0, fontSize: 12, color: "rgba(126,184,255,0.75)" }}>
            {t("spending_modal_admin_note")}
          </p>
        ) : null}

        {error ? (
          <div
            style={{
              fontSize: 12,
              color: "#e06c6c",
              padding: "8px 10px",
              borderRadius: 6,
              background: "rgba(224,108,108,0.08)",
            }}
          >
            {error}
          </div>
        ) : null}
      </div>
    </CosmosModal>
  );
}

const secondaryBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "1px solid rgba(126,184,255,0.35)",
  color: "#e8f0ff",
  borderRadius: 8,
  padding: "8px 14px",
  fontSize: 12,
  cursor: "pointer",
};

const primaryBtnStyle: React.CSSProperties = {
  background: "rgba(126,184,255,0.9)",
  border: "none",
  color: "#04070d",
  borderRadius: 8,
  padding: "8px 14px",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
};
